import React, { useState, useEffect } from "react";
import {
  Grid2,
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Chip
} from "@mui/material";
import { usePermission } from "../authentication/permission-providor/UserPermission";
import { useDispatch, useSelector } from "react-redux";
import AddMedia from "./AddMedia";
import { getAssets, removeAsset } from "../../actions/mediaAction";
import { DataGrid } from "@mui/x-data-grid";
import AddButton from "../../themes/overrides/AddButton";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchField from "../../themes/overrides/SearchField";
import EditIcon from "../../components/icons/EditIcon";
import RemoveIcon from "../../components/icons/RemoveIcon";
import MediaIcon from "../../assets/images/brush.svg";
import ListIcon from "../../components/icons/ListIcon";
import GridIcon from "../../components/icons/GridIcon";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import LoadingOverlay from "../../components/LoadingOverlay";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { useParams } from 'react-router-dom';

const MediaList = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteAsset, setSelectedDeleteAsset] = useState(null);

  const { workspaceId } = useParams();
  const assets = useSelector((state) => state.assets.assets);
  const uploadSuccess = useSelector((state) => state.assets.success)
  const [shouldRefresh, setShouldRefresh] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewMode, setViewMode] = useState("list");
  const [gridColor, setGridColor] = useState("#6B6B6B");
  const [listColor, setListColor] = useState("#6B6B6B");

  const openAddMediaModal = () => {
    setModalOpen(true);
    setEditMode(false);
    setSelectedAsset(null);
  };

  useEffect(() => {
    if (shouldRefresh || uploadSuccess) {
      setLoading(true);
      Promise.all([
        dispatch(getAssets(workspaceId))
      ]).finally(() => setLoading(false));

      setShouldRefresh(false);
    }
  }, [shouldRefresh, uploadSuccess, workspaceId, dispatch]);

  const handleEdit = (id) => {
    const assetToEdit = assets.find((asset) => asset._id === id);
    if (assetToEdit) {
      setSelectedAsset(assetToEdit);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDelete = (assetId) => {
    setSelectedDeleteAsset(assetId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const success = await dispatch(removeAsset(selectedDeleteAsset));
    if (success) {
      setShouldRefresh(true);
    }
    setDeleteModalOpen(false);
    setSelectedDeleteAsset(null);
  };

  const handleSubmitSuccess = () => {
    setModalOpen(false);
    setShouldRefresh(true);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const handleToggleView = (view) => {
    if (view === "List") {
      setViewMode("list");
      setListColor("#006D70");
      setGridColor("#6B6B6B");
    } else {
      setViewMode("grid");
      setGridColor("#006D70");
      setListColor("#6B6B6B");
    }
  };

  const columns = [
    { field: "id", headerName: "id", width: 50 },
    {
      field: "url",
      headerName: "Image",
      width: 100,
      renderCell: (params) => (
        <img
          src={params.value}
          alt={`Asset ${params.row.id}`}
          style={{
            width: '50px',
            height: '50px',
            objectFit: 'cover',
            borderRadius: '8px',
            border: '1px solid #ddd',
            padding: '2px',
          }}
        />
      ),
    },
    { field: "title", headerName: "File Name", flex: 1 },
    { field: "type", headerName: "Type", flex: 1 },

    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row._id)}
            aria-label="edit"
            disabled={!hasPermission('editor')}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleDelete(params.row._id)}
            aria-label="delete"
            disabled={!hasPermission('admin')}
          >
            <RemoveIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const rows = assets.map((assets, index) => ({
    id: index,
    ...assets,
  }));

  const filteredRows = assets
    .filter(
      (asset) =>
        asset.title.toLowerCase().includes(searchTerm)
    )
    .map((asset, index) => ({
      id: index + 1,
      ...asset,
    }));



  return (
    loading ? <LoadingOverlay /> :
      <>
        <Grid2 container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid2 container size={12} display="flex" justifyContent="space-between">
            <Grid2 size={{ xs: 12, md: 8 }} display='flex' alignItems="flex-start" sx={{ marginLeft: { xs: 4, md: 0 } }}>
              <Box component="img" sx={{ width: { xs: 32, md: 40 }, marginRight: { xs: 2, md: 4 } }} src={MediaIcon} />
              <Typography variant={"h1"}>All Media</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }} offset={{ xs: 4, md: 0 }} display="flex" justifyContent="flex-end">
              <AddButton
                label="Add Media"
                sx={{ mr: 1 }}
                onClick={openAddMediaModal}
                disabled={!hasPermission('editor')}
              />
            </Grid2>
          </Grid2>
          
          <Grid2 container size={12} spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Grid2 size={12} sx={{ width: "100%", minWidth: { md: '100%' } }} >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexGrow: 1
                }}>

                <Box sx={{ width: { md: 300 } }}>
                  <SearchField
                    placeholder="Search Media"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ border: '1px solid #6B6B6B', borderRadius: 2, background: 'white' }}>
                  <IconButton onClick={() => handleToggleView("List")}>
                    <ListIcon toggleColor={listColor} />
                  </IconButton>
                  <IconButton onClick={() => handleToggleView("Grid")}>
                    <GridIcon toggleColor={gridColor} />
                  </IconButton>
                </Box>
              </Box>
            </Grid2>
            <Grid2 size={12} sx={{ overflowX: 'auto' }}>
              <Paper elevation={2} sx={{ padding: 2, marginBottom: 2, marginTop: { md: 2 }, overflowX: 'auto' }}>
                {filteredRows.length > 0 ? (
                  viewMode === "list" ? (
                    <Box
                      sx={{
                        width: '100%',
                        overflowX: 'auto',
                      }}
                    >
                      <DataGrid
                        disableRowSelectionOnClick
                        rows={filteredRows}
                        columns={columns}
                        disableColumnSelector
                        initialState={{
                          pagination: {
                            paginationModel: {
                              pageSize: 15,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiDataGrid-cell:focus': {
                            outline: 'none'
                          },
                        }}
                        pageSizeOptions={[15]}
                      />
                    </Box>
                  ) : (
                    <Grid2 container spacing={2} sx={{ flexWrap: 'wrap' }}>
                      {filteredRows.map((data, i) => (
                        <Grid2 key={i} size={{ xs: 12, md: 3 }} paddingRight={1}>
                          {/* Content Card */}
                          <Box sx={{ border: "1px solid #ddd", borderRadius: 2 }}>
                            <Box
                              component="img"
                              sx={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'cover',
                              }}
                              src={data.url}
                              alt={data.title}
                            />
                            <Box p={2}>
                              <Typography variant="body1">{data.title}</Typography>
                              <Typography variant="body2">{data.type}</Typography>
                            </Box>
                            <Box paddingLeft={2} display="flex">
                              <IconButton
                                color="primary"
                                onClick={() => handleEdit(data._id)}
                                aria-label="edit"
                                disabled={!hasPermission('editor')}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="primary"
                                onClick={() => handleDelete(data._id)}
                                aria-label="delete"
                                disabled={!hasPermission('admin')}
                              >
                                <RemoveIcon />
                              </IconButton>

                            </Box>
                          </Box>
                        </Grid2>
                      ))
                      }
                    </Grid2>
                  )) : (
                  <Typography align="center">No Contents</Typography>
                )}
              </Paper>
            </Grid2>
          </Grid2>
          <AddMedia
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
            onSubmitSuccess={handleSubmitSuccess}
            editMode={editMode}
            asset={selectedAsset}
          />
        </Grid2>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          text="Are you sure you want to delete this asset?"
          onConfirm={handleConfirmDelete}
          deleteButtonLabel="Delete"
        />
      </>
  );
};

export default MediaList;
